/**
 * HTTP-backed implementation of `MarketCategoriesService`.
 *
 * Talks to the PartnerProgramApi endpoints under `/api/ProgramConfig`.
 * The backend stores categories as a flat `(categoryName, country)` table
 * and thresholds as a separate `(marketCategory, tierName, thresholdUsd)`
 * table; this service joins them into the grouped `MarketCategory` shape
 * the UI expects.
 *
 */

import type { ApiService } from './apiService';
import {
  MarketCategoriesError,
  ZERO_THRESHOLDS,
  type LastUpdatedInfo,
  type MarketCategoriesService,
  type MarketCategory,
  type PendingCountryDiff,
  type PendingMarketCategoryInfo,
  type PendingThresholdsInfo,
  type TierThresholds,
} from './marketCategoriesService';
import { TIER_KEYS, TIER_LABELS, tierKeyFromName } from '../constants/tiers';

interface MarketCategoryRow {
  categoryName: string;
  country: string;
}

interface ThresholdRow {
  marketCategory: string;
  tierName: string;
  thresholdUsd: number;
}

interface PendingMarketCategoryChangeRow {
  scopeName: string;
  newScopeName: string | null;
  operation: string;
  payload: MarketCategoryRow[] | null;
  createdAt: string;
  createdBy: string;
}

interface PendingThresholdChangeRow {
  scopeName: string;
  operation: string;
  payload: ThresholdRow[] | null;
  createdAt: string;
  createdBy: string;
}

interface LastAppliedInfoRow {
  scopeName: string;
  lastAppliedAt: string;
  lastAppliedBy: string;
}

const normalize = (s: string) => s.trim().toLowerCase();

const slugify = (name: string) =>
  name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '') || 'category';

export class HttpMarketCategoriesService implements MarketCategoriesService {
  private readonly api: ApiService;
  private readonly idToName = new Map<string, string>();
  private idCounter = 0;
  private readonly countriesById = new Map<string, string[]>();
  private readonly thresholdsById = new Map<string, TierThresholds>();
  /**
   * Set of normalized names that exist in the committed SQL store. Populated
   * on each `list()` call and used by `updateName` / `remove` to choose
   * between the "stage a real backend change" path and the "this row only
   * exists in pending state, so cancel that pending" path.
   */
  private readonly committedNames = new Set<string>();
  /**
   * For pending=create rows, the IDs whose name has no SQL backing. Used by
   * `updateName` to handle the rename-of-pending-create case (cancel old
   * pending + stage a new create with the new name).
   */
  private readonly pendingOnlyIds = new Set<string>();
  /**
   * For rows that have ANY pending change, the scope name under which the
   * pending lives in Cosmos. Equals the row's display name for create /
   * update / delete pendings, but equals the row's PREVIOUS name for rename
   * pendings (since the rename pending is partitioned under the old scope).
   * Used by `cancelPending(id)` to address the right Cosmos record without
   * the caller needing pending context.
   */
  private readonly pendingScopeById = new Map<string, string>();
  /**
   * For rows that have a pending threshold change, the scope name under which
   * that pending lives in Cosmos. Used by `cancelPendingThresholds(id)`.
   */
  private readonly thresholdPendingScopeById = new Map<string, string>();

  constructor(api: ApiService) {
    this.api = api;
  }

  async list(): Promise<MarketCategory[]> {
    const [rows, thresholdRows, pendingRows, pendingThresholdRows, lastAppliedCategoryRows, lastAppliedThresholdRows] = await Promise.all([
      this.api.get<MarketCategoryRow[]>('/ProgramConfig/marketCategories'),
      this.api.get<ThresholdRow[]>('/ProgramConfig/marketThresholds'),
      this.api
        .get<PendingMarketCategoryChangeRow[]>('/ProgramConfig/pendingChanges/marketCategories')
        .catch((err) => {
          // Non-fatal: if the pending-changes endpoint fails (e.g. Cosmos firewall),
          // we still render the committed SQL state. The user just won't see a badge.
          console.warn('[HttpMarketCategoriesService] Failed to load pending changes:', err);
          return [] as PendingMarketCategoryChangeRow[];
        }),
      this.api
        .get<PendingThresholdChangeRow[]>('/ProgramConfig/pendingChanges/marketThresholds')
        .catch((err) => {
          console.warn('[HttpMarketCategoriesService] Failed to load pending threshold changes:', err);
          return [] as PendingThresholdChangeRow[];
        }),
      // Last-applied lookups feed the "Last updated" column. Same graceful-
      // degradation contract as pending: any failure returns an empty list so
      // the table still renders without the column being populated.
      this.api
        .get<LastAppliedInfoRow[]>('/ProgramConfig/lastApplied/marketCategories')
        .catch((err) => {
          console.warn('[HttpMarketCategoriesService] Failed to load last-applied for categories:', err);
          return [] as LastAppliedInfoRow[];
        }),
      this.api
        .get<LastAppliedInfoRow[]>('/ProgramConfig/lastApplied/marketThresholds')
        .catch((err) => {
          console.warn('[HttpMarketCategoriesService] Failed to load last-applied for thresholds:', err);
          return [] as LastAppliedInfoRow[];
        }),
    ]);

    const thresholdsByName = new Map<string, TierThresholds>();
    for (const row of thresholdRows ?? []) {
      const tierKey = tierKeyFromName(row.tierName);
      if (!tierKey) {
        console.warn(
          `[HttpMarketCategoriesService] Ignoring unknown tier "${row.tierName}" for "${row.marketCategory}".`,
        );
        continue;
      }
      const key = normalize(row.marketCategory);
      const current = thresholdsByName.get(key) ?? { ...ZERO_THRESHOLDS };
      current[tierKey] = Number(row.thresholdUsd) || 0;
      thresholdsByName.set(key, current);
    }

    const order: string[] = [];
    const countriesByName = new Map<string, string[]>();
    for (const row of rows ?? []) {
      const key = normalize(row.categoryName);
      if (!countriesByName.has(key)) {
        countriesByName.set(key, []);
        order.push(row.categoryName);
      }
      countriesByName.get(key)!.push(row.country);
    }

    // Snapshot the committed (SQL) names. Used later by `updateName` /
    // `remove` to choose between the "stage a real backend change" path and
    // the "this row only exists in pending state, so cancel that pending"
    // path. Captured BEFORE merge so that pending creates don't sneak in.
    this.committedNames.clear();
    for (const name of countriesByName.keys()) {
      this.committedNames.add(name);
    }

    // Snapshot the committed (SQL) country lists keyed by SQL name. The merge
    // below mutates `countriesByName` (renames move entries between keys,
    // updates overwrite payloads) so the original SQL state would be lost
    // otherwise. We use this snapshot at the very end to compute the per-row
    // diff between "what SQL has now" and "what the merged view will show
    // once pendings apply" — which is the only diff the user cares about.
    const sqlCountriesByName = new Map<string, string[]>();
    for (const [k, v] of countriesByName) {
      sqlCountriesByName.set(k, [...v]);
    }

    // Index pending changes by scope (case-insensitive). The backend already
    // deduplicates by marking older pendings as Superseded, so we expect at
    // most one entry per scope — but we guard anyway by keeping the most recent
    // CreatedAt if duplicates ever slip through.
    const pendingByName = new Map<string, PendingMarketCategoryChangeRow>();
    for (const row of pendingRows ?? []) {
      const key = normalize(row.scopeName);
      const existing = pendingByName.get(key);
      if (!existing || new Date(row.createdAt) > new Date(existing.createdAt)) {
        pendingByName.set(key, row);
      }
    }

    // Apply each pending to the merged display state. The output map is keyed
    // by the *current display name* of the row, which equals `scopeName` for
    // create/update/delete pendings and `newScopeName` for renames. Country
    // diffs (added/removed) are computed against the committed SQL list so
    // the UI can color-code each chip.
    //
    // IMPORTANT: process renames first. Otherwise an Update pending staged
    // under the new name (which happens when the user edits countries on a
    // freshly-renamed row before the next refresh) is processed before the
    // Rename and gets treated as a brand-new Create — which (a) duplicates
    // the row in `order` once the Rename also adds it, and (b) shows every
    // country green because the committed-set looks empty. By applying
    // renames first the new name is already in `countriesByName`, so a
    // subsequent Update on it sees the renamed countries as the baseline and
    // computes a proper diff.
    const pendingInfoByName = new Map<string, PendingMarketCategoryInfo>();
    const pendingDiffByName = new Map<string, PendingCountryDiff>();
    const pendingOnlyKeys = new Set<string>();
    // Tracks keys whose row was introduced/repositioned by a Rename pending.
    // When a later Update lands on the same key, we keep the rename marker
    // (the rename is the "primary" change) but still compute a country diff
    // against the renamed-source countries so the UI shows added/removed
    // chips on top of the orange renamed name.
    const renamedKeys = new Map<string, { previousName: string; createdAt: string; createdBy: string }>();

    const sortedPendings = [...pendingByName.entries()].sort(([, a], [, b]) => {
      const aRename = (a.operation ?? '').toLowerCase() === 'rename' ? 0 : 1;
      const bRename = (b.operation ?? '').toLowerCase() === 'rename' ? 0 : 1;
      return aRename - bRename;
    });

    for (const [scopeKey, pending] of sortedPendings) {
      const opLc = (pending.operation ?? 'update').toLowerCase();
      const committedCountries = countriesByName.get(scopeKey) ?? null;
      const knownInSql = committedCountries !== null;
      const payloadCountries = (pending.payload ?? [])
        .map((p) => p.country)
        .filter((c): c is string => !!c);

      if (opLc === 'rename' && pending.newScopeName) {
        const newKey = normalize(pending.newScopeName);
        // Prefer the rename's payload when present (it carries inherited
        // country edits made before the rename was staged); fall back to the
        // SQL committed countries, then to the empty list.
        const renameCountries = payloadCountries.length > 0
          ? payloadCountries
          : (knownInSql ? committedCountries! : []);

        if (knownInSql) {
          // Move the row in place: keep its position in `order` but swap the
          // display name so the user sees the new name where the old row used
          // to be.
          countriesByName.delete(scopeKey);
          countriesByName.set(newKey, renameCountries);
          const orderIdx = order.findIndex((n) => normalize(n) === scopeKey);
          if (orderIdx >= 0) {
            order[orderIdx] = pending.newScopeName;
          } else {
            order.push(pending.newScopeName);
          }
          // Carry threshold lookup over to the new key so the merged row
          // still sees its tier values until the applier rewrites SQL.
          const sqlThresholds = thresholdsByName.get(scopeKey);
          if (sqlThresholds) {
            thresholdsByName.delete(scopeKey);
            thresholdsByName.set(newKey, sqlThresholds);
          }
        } else {
          // Edge case: rename pending without SQL backing. Render under the
          // new name with whatever payload we have.
          countriesByName.set(newKey, renameCountries);
          order.push(pending.newScopeName);
          pendingOnlyKeys.add(newKey);
        }

        pendingInfoByName.set(newKey, {
          operation: 'rename',
          createdAt: pending.createdAt,
          createdBy: pending.createdBy,
          previousName: pending.scopeName,
        });
        renamedKeys.set(newKey, {
          previousName: pending.scopeName,
          createdAt: pending.createdAt,
          createdBy: pending.createdBy,
        });
        continue;
      }

      if (opLc === 'delete') {
        // Keep the committed countries visible so the user sees what will be
        // removed; just stamp the delete marker. If somehow the scope isn't
        // in SQL (shouldn't happen — frontend uses cancel-pending for those),
        // skip it.
        if (knownInSql) {
          pendingInfoByName.set(scopeKey, {
            operation: 'delete',
            createdAt: pending.createdAt,
            createdBy: pending.createdBy,
          });
        }
        continue;
      }

      // create / update (or unknown -> treat as update). Three sub-cases:
      //   1. Key was introduced by an earlier Rename → keep rename marker,
      //      overwrite countries with payload.
      //   2. Key exists in SQL → standard Update.
      //   3. Key not anywhere → standard Create.
      const renameContext = renamedKeys.get(scopeKey);

      if (!knownInSql && !renameContext) {
        countriesByName.set(scopeKey, payloadCountries);
        order.push(pending.scopeName);
        pendingOnlyKeys.add(scopeKey);
      } else {
        countriesByName.set(scopeKey, payloadCountries);
      }

      if (renameContext) {
        // Preserve the rename marker; do not downgrade to 'update'.
        pendingInfoByName.set(scopeKey, {
          operation: 'rename',
          createdAt: renameContext.createdAt,
          createdBy: renameContext.createdBy,
          previousName: renameContext.previousName,
        });
      } else {
        pendingInfoByName.set(scopeKey, {
          operation: knownInSql ? 'update' : 'create',
          createdAt: pending.createdAt,
          createdBy: pending.createdBy,
        });
      }
    }

    // Compute final per-row country diff against SQL committed state. Done
    // after all pendings have been merged so the diff reflects the
    // *cumulative* delta (e.g. rename inheriting an Update + a follow-up
    // Update under the new name) rather than the delta of just the last
    // pending applied. The SQL baseline is looked up by the row's ORIGINAL
    // name (before any rename), which is `previousName` for renamed rows
    // and the row's current name otherwise.
    for (const [key, info] of pendingInfoByName.entries()) {
      const sqlKey = info.operation === 'rename' && info.previousName
        ? normalize(info.previousName)
        : key;
      const sqlCountries = sqlCountriesByName.get(sqlKey) ?? [];
      const finalCountries = countriesByName.get(key) ?? [];
      // For delete pendings the merged countries equal SQL; the diff is
      // intentionally suppressed (the row name is already styled red+strike,
      // showing it as "all removed" via per-country chips would be visual
      // noise).
      if (info.operation === 'delete') continue;
      const sqlSet = new Set(sqlCountries.map(normalize));
      const finalSet = new Set(finalCountries.map(normalize));
      const added = finalCountries.filter((c) => !sqlSet.has(normalize(c)));
      const removed = sqlCountries.filter((c) => !finalSet.has(normalize(c)));
      if (added.length > 0 || removed.length > 0) {
        pendingDiffByName.set(key, { added, removed });
      }
    }

    const pendingThresholdsByScope = new Map<string, PendingThresholdChangeRow>();
    for (const row of pendingThresholdRows ?? []) {
      const key = normalize(row.scopeName);
      const existing = pendingThresholdsByScope.get(key);
      if (!existing || new Date(row.createdAt) > new Date(existing.createdAt)) {
        pendingThresholdsByScope.set(key, row);
      }
    }

    // Build lookup maps for the "Last updated" column. Keyed by lowercased
    // scope name to match the rest of this service. Backend already returns
    // at most one row per scope (max AppliedAt), so we don't need to dedupe.
    const lastUpdatedCategoryByName = new Map<string, LastUpdatedInfo>();
    for (const row of lastAppliedCategoryRows ?? []) {
      lastUpdatedCategoryByName.set(normalize(row.scopeName), {
        at: row.lastAppliedAt,
        by: row.lastAppliedBy,
      });
    }
    const lastUpdatedThresholdsByName = new Map<string, LastUpdatedInfo>();
    for (const row of lastAppliedThresholdRows ?? []) {
      lastUpdatedThresholdsByName.set(normalize(row.scopeName), {
        at: row.lastAppliedAt,
        by: row.lastAppliedBy,
      });
    }

    const buildPendingThresholds = (
      row: PendingThresholdChangeRow,
      sqlThresholds: TierThresholds,
    ): { thresholds: TierThresholds; info: PendingThresholdsInfo } => {
      const merged: TierThresholds = { ...sqlThresholds };
      for (const item of row.payload ?? []) {
        const tierKey = tierKeyFromName(item.tierName);
        if (!tierKey) continue;
        merged[tierKey] = Number(item.thresholdUsd) || 0;
      }
      return {
        thresholds: merged,
        info: {
          operation: 'update',
          createdAt: row.createdAt,
          createdBy: row.createdBy,
          previousThresholds: { ...sqlThresholds },
        },
      };
    };

    this.idToName.clear();
    this.countriesById.clear();
    this.thresholdsById.clear();
    this.pendingOnlyIds.clear();
    this.pendingScopeById.clear();
    this.thresholdPendingScopeById.clear();
    this.idCounter = 0;

    const result: MarketCategory[] = order.map((name) => {
      const id = this.allocateId(name);
      const key = normalize(name);
      const countries = countriesByName.get(key) ?? [];
      const sqlThresholds = thresholdsByName.get(key) ?? { ...ZERO_THRESHOLDS };
      let thresholds = sqlThresholds;
      let pendingThresholds: PendingThresholdsInfo | undefined;

      const pendingForRow = pendingInfoByName.get(key);
      const previousNameKey = pendingForRow?.operation === 'rename' && pendingForRow.previousName
        ? normalize(pendingForRow.previousName)
        : null;
      const thresholdPending =
        pendingThresholdsByScope.get(key) ??
        (previousNameKey ? pendingThresholdsByScope.get(previousNameKey) : undefined);
      if (thresholdPending) {
        const built = buildPendingThresholds(thresholdPending, sqlThresholds);
        thresholds = built.thresholds;
        pendingThresholds = built.info;
      }

      this.countriesById.set(id, [...countries]);
      this.thresholdsById.set(id, { ...thresholds });
      if (pendingOnlyKeys.has(key)) {
        this.pendingOnlyIds.add(id);
      }
      const pending = pendingForRow;
      // Rename pendings live under the OLD scope name in Cosmos; everything
      // else lives under the row's display name.
      if (pending) {
        const cosmosScope = pending.operation === 'rename' && pending.previousName
          ? pending.previousName
          : name;
        this.pendingScopeById.set(id, cosmosScope);
      }

      if (thresholdPending) {
        this.thresholdPendingScopeById.set(id, thresholdPending.scopeName);
      }
      const diff = pendingDiffByName.get(key);
      // For renamed rows, look up last-updated under both the new and old
      // names (the partition key in Cosmos still reflects the pre-rename
      // name until another change is applied to the new one). The new key
      // wins when both are present; otherwise we surface the previous
      // history so the row doesn't suddenly look like "never applied".
      const previousKey =
        pending?.operation === 'rename' && pending.previousName
          ? normalize(pending.previousName)
          : null;
      const lastUpdatedCategory =
        lastUpdatedCategoryByName.get(key) ??
        (previousKey ? lastUpdatedCategoryByName.get(previousKey) : undefined);
      const lastUpdatedThresholds =
        lastUpdatedThresholdsByName.get(key) ??
        (previousKey ? lastUpdatedThresholdsByName.get(previousKey) : undefined);
      return {
        id,
        name,
        countries: [...countries],
        thresholds: { ...thresholds },
        ...(pending ? { pending } : {}),
        ...(diff ? { pendingCountryDiff: { added: [...diff.added], removed: [...diff.removed] } } : {}),
        ...(pendingThresholds ? { pendingThresholds: { ...pendingThresholds, previousThresholds: { ...pendingThresholds.previousThresholds } } } : {}),
        ...(lastUpdatedCategory ? { lastUpdatedCategory: { ...lastUpdatedCategory } } : {}),
        ...(lastUpdatedThresholds ? { lastUpdatedThresholds: { ...lastUpdatedThresholds } } : {}),
      };
    });

    return result;
  }

  async create({ name }: { name: string }): Promise<MarketCategory> {
    const trimmed = name.trim();
    if (!trimmed) {
      throw new MarketCategoriesError('EMPTY_NAME', 'Category name is required.');
    }
    if (this.findIdByName(trimmed)) {
      throw new MarketCategoriesError(
        'DUPLICATE_NAME',
        `A category named "${trimmed}" already exists.`,
      );
    }

    // `create` only allocates a local id; the row is persisted on the
    // follow-up `updateCountries` call (the backend has no create endpoint
    // and its PUT requires at least one country).
    const id = this.allocateId(trimmed);
    this.countriesById.set(id, []);
    this.thresholdsById.set(id, { ...ZERO_THRESHOLDS });
    return {
      id,
      name: trimmed,
      countries: [],
      thresholds: { ...ZERO_THRESHOLDS },
    };
  }

  async updateName(id: string, name: string): Promise<MarketCategory> {
    const trimmed = name.trim();
    if (!trimmed) {
      throw new MarketCategoriesError('EMPTY_NAME', 'Category name is required.');
    }
    const current = this.idToName.get(id);
    if (!current) {
      throw new MarketCategoriesError('NOT_FOUND', `Category ${id} not found.`);
    }
    const conflict = this.findIdByName(trimmed);
    if (conflict && conflict !== id) {
      throw new MarketCategoriesError(
        'DUPLICATE_NAME',
        `A category named "${trimmed}" already exists.`,
      );
    }

    const cosmosScope = this.pendingScopeById.get(id) ?? current;
    const sqlName = this.committedNames.has(normalize(cosmosScope))
      ? cosmosScope
      : this.committedNames.has(normalize(current))
        ? current
        : null;

    if (sqlName !== null) {
      // Committed (possibly with a pending Update or Rename on top): stage a
      // Rename pending. We send the *currently displayed name* as the URL
      // path (which may be a post-rename name not yet in SQL); the backend's
      // pending lookup matches on either ScopeName or NewScopeName so it
      // finds any prior Rename either way. Sending the SQL-side name here
      // would block the user from renaming back to the original committed
      // name (the early "must differ" check would fire because the URL path
      // and the new name would be identical).
      await this.api.post(
        `/ProgramConfig/marketCategories/${encodeURIComponent(current)}/rename`,
        { newName: trimmed },
      );
    } else if (this.pendingOnlyIds.has(id)) {
      // The row only exists because of a pending Create. Renaming it through
      // the rename endpoint would point at a SQL row that isn't there yet
      // (the applier would fail). Instead, cancel the pending under its
      // tracked scope and re-stage the create under the new name with the
      // same countries.
      const countries = this.countriesById.get(id) ?? [];
      await this.api.delete(
        `/ProgramConfig/pendingChanges/marketCategories/${encodeURIComponent(cosmosScope)}`,
      );
      if (countries.length > 0) {
        await this.api.put(
          `/ProgramConfig/marketCategories/${encodeURIComponent(trimmed)}`,
          { items: countries.map((country) => ({ categoryName: trimmed, country })) },
        );
      }
    }
    // else: row is brand-new in this session (created via `create()` and not
    // yet persisted by `updateCountries`). Nothing to rename on the backend.

    this.idToName.set(id, trimmed);
    return {
      id,
      name: trimmed,
      countries: [...(this.countriesById.get(id) ?? [])],
      thresholds: { ...(this.thresholdsById.get(id) ?? ZERO_THRESHOLDS) },
    };
  }

  async updateCountries(id: string, countries: string[]): Promise<MarketCategory> {
    const name = this.idToName.get(id);
    if (!name) {
      throw new MarketCategoriesError('NOT_FOUND', `Category ${id} not found.`);
    }

    const lcSet = new Set<string>();
    for (const country of countries) {
      const lc = normalize(country);
      if (lcSet.has(lc)) {
        throw new MarketCategoriesError(
          'DUPLICATE_COUNTRY',
          `"${country}" was listed twice.`,
        );
      }
      lcSet.add(lc);
    }
    for (const [otherId, otherCountries] of this.countriesById.entries()) {
      if (otherId === id) continue;
      for (const country of otherCountries) {
        if (lcSet.has(normalize(country))) {
          const otherName = this.idToName.get(otherId) ?? 'another category';
          throw new MarketCategoriesError(
            'DUPLICATE_COUNTRY',
            `"${country}" already belongs to "${otherName}".`,
          );
        }
      }
    }

    if (countries.length === 0) {
      throw new MarketCategoriesError(
        'EMPTY_COUNTRIES',
        'At least one country is required.',
      );
    }

    await this.api.put(`/ProgramConfig/marketCategories/${encodeURIComponent(name)}`, {
      items: countries.map((country) => ({ categoryName: name, country })),
    });

    this.countriesById.set(id, [...countries]);
    return {
      id,
      name,
      countries: [...countries],
      thresholds: { ...(this.thresholdsById.get(id) ?? ZERO_THRESHOLDS) },
    };
  }

  async updateThresholds(id: string, thresholds: TierThresholds): Promise<MarketCategory> {
    const name = this.idToName.get(id);
    if (!name) {
      throw new MarketCategoriesError('NOT_FOUND', `Category ${id} not found.`);
    }

    await this.api.put(`/ProgramConfig/marketThresholds/${encodeURIComponent(name)}`, {
      items: TIER_KEYS.map((key) => ({
        marketCategory: name,
        tierName: TIER_LABELS[key],
        thresholdUsd: thresholds[key],
      })),
    });

    this.thresholdsById.set(id, { ...thresholds });
    return {
      id,
      name,
      countries: [...(this.countriesById.get(id) ?? [])],
      thresholds: { ...thresholds },
    };
  }

  async remove(id: string): Promise<void> {
    const name = this.idToName.get(id);
    if (!name) {
      throw new MarketCategoriesError('NOT_FOUND', `Category ${id} not found.`);
    }

    const cosmosScope = this.pendingScopeById.get(id) ?? name;
    const sqlName = this.committedNames.has(normalize(cosmosScope))
      ? cosmosScope
      : this.committedNames.has(normalize(name))
        ? name
        : null;

    if (sqlName !== null) {
      // Real delete: stage a Delete pending. The backend supersede logic
      // takes care of clearing any prior Update/Rename pending on this scope.
      await this.api.delete(`/ProgramConfig/marketCategories/${encodeURIComponent(sqlName)}`);
    } else {
      // Pending-only row (a create that hasn't synced to SQL yet). Cancel
      // the pending so it disappears from Cosmos rather than being applied.
      // Use the tracked Cosmos scope so we hit the right partition even
      // if the pending got renamed at some point.
      await this.api.delete(
        `/ProgramConfig/pendingChanges/marketCategories/${encodeURIComponent(cosmosScope)}`,
      );
    }

    // Local cleanup. The caller is expected to refresh() so the merged list
    // reflects backend reality (delete-pending rows reappear with strikethrough;
    // pending-create rows vanish entirely).
    this.idToName.delete(id);
    this.countriesById.delete(id);
    this.thresholdsById.delete(id);
    this.pendingOnlyIds.delete(id);
    this.pendingScopeById.delete(id);
    this.thresholdPendingScopeById.delete(id);
  }

  async cancelPending(id: string): Promise<void> {
    const name = this.idToName.get(id);
    if (!name) {
      throw new MarketCategoriesError('NOT_FOUND', `Category ${id} not found.`);
    }
    // Use the tracked Cosmos scope (oldName for renames, current name for
    // everything else). Falls back to the display name when no pending was
    // recorded, which mirrors the backend's idempotent behavior anyway.
    const scope = this.pendingScopeById.get(id) ?? name;
    await this.api.delete(
      `/ProgramConfig/pendingChanges/marketCategories/${encodeURIComponent(scope)}`,
    );
    this.pendingScopeById.delete(id);
    // The caller is expected to refresh() so the merged list reflects the
    // cancellation (e.g. a renamed row reverts to its old name; a delete
    // marker disappears; a created-only row vanishes entirely).
  }

  async cancelPendingThresholds(id: string): Promise<void> {
    const name = this.idToName.get(id);
    if (!name) {
      throw new MarketCategoriesError('NOT_FOUND', `Category ${id} not found.`);
    }

    const scope = this.thresholdPendingScopeById.get(id) ?? name;
    await this.api.delete(
      `/ProgramConfig/pendingChanges/marketThresholds/${encodeURIComponent(scope)}`,
    );
    this.thresholdPendingScopeById.delete(id);
  }

  private allocateId(name: string): string {
    const id = `mc-${slugify(name)}-${++this.idCounter}`;
    this.idToName.set(id, name);
    return id;
  }

  private findIdByName(name: string): string | undefined {
    const target = normalize(name);
    for (const [id, n] of this.idToName.entries()) {
      if (normalize(n) === target) return id;
    }
    return undefined;
  }
}
